import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

/** One day's bar: the day, the number it stands for, and what the tooltip says. */
export interface DailyBar {
  day: string;
  value: number;
  title: string;
}

/**
 * A per-day bar chart, one bar a day, tallest day full height.
 *
 * The engagement board, the abuse board and the event page each drew the same
 * chart - a row of flex columns and a height percentage - three times over,
 * with three copies of the bar styles and three barHeight() methods. The
 * question each asks is the same: "was there a day that stood out". This is
 * that chart once. Heights are relative to the busiest day so a small
 * baseline stays readable, and a bar never drops below a sliver so the day is
 * still visibly there.
 */
@Component({
  selector: 'app-daily-bars',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="chart">
      <div class="chart-head">
        <span>{{ title }}</span>
        <span class="meta"><ng-content></ng-content></span>
      </div>
      <div class="bars" [class.labelled]="labels">
        <div class="bar-col" *ngFor="let b of bars" [title]="b.title">
          <div class="bar" [style.height.%]="height(b.value)" [class.zero]="!b.value"></div>
          <div class="bar-label" *ngIf="labels">{{ b.day | date: 'd/M' }}</div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .chart {
        background: #fff;
        border-radius: 16px;
        padding: 16px 18px 12px;
        margin-bottom: 18px;
        box-shadow: 0 4px 12px rgba(18, 51, 47, 0.07);
      }
      .chart-head {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        font: 700 11px 'Bricolage Grotesque', 'Instrument Sans', system-ui, sans-serif;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: #8a5a08;
      }
      .chart-head .meta {
        text-transform: none;
        letter-spacing: 0;
        color: #8f8c88;
        font-weight: 400;
        font-family: 'Instrument Sans', system-ui, sans-serif;
        font-size: 12px;
      }
      .bars {
        display: flex;
        align-items: flex-end;
        gap: 3px;
        height: 80px;
        margin-top: 14px;
      }
      .bars.labelled {
        gap: 6px;
        height: 104px;
      }
      .bar-col {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        height: 100%;
      }
      .bar {
        width: 100%;
        background: #1f6f66;
        border-radius: 3px 3px 0 0;
        min-height: 2px;
      }
      .bar.zero {
        background: rgba(18, 51, 47, 0.1);
      }
      .bar-label {
        font-size: 10px;
        color: #8f8c88;
        text-align: center;
        margin-top: 5px;
      }
    `,
  ],
})
export class DailyBarsComponent {
  @Input() title = '';
  @Input() bars: DailyBar[] = [];
  /** Date labels under the bars; only worth it when there are few enough to read. */
  @Input() labels = false;

  height(value: number): number {
    const peak = Math.max(1, ...this.bars.map((b) => b.value));
    return Math.max(3, Math.round((value / peak) * 100));
  }
}
